import { useState } from "react";
import { Image, Trash2, Maximize2, X } from "lucide-react";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Dialog, DialogContent } from "../ui/dialog";
import { ImageData } from "../../api/processImages";
import { useTranslation } from "react-i18next";

interface ImageViewerProps {
  image: ImageData;
  onDelete?: () => void;
  compact?: boolean;
  readOnly?: boolean;
  showMetadata?: boolean; // Show dimensions/size (only during upload/edit)
  hideThumbnail?: boolean; // Hide thumbnail, show clickable link instead
}

export const ImageViewer = ({
  image,
  onDelete,
  compact = false,
  readOnly = false,
  showMetadata = false,
  hideThumbnail = false,
}: ImageViewerProps) => {
  const { t } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  const handleDelete = () => {
    onDelete?.();
    setShowDeleteDialog(false);
  };

  // Determine if delete button should be shown
  const showDeleteButton = !readOnly && onDelete !== undefined;

  // Format file size
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <div
        className={
          compact
            ? "relative rounded-md overflow-hidden cursor-pointer group"
            : "border rounded-lg p-3 bg-muted/20 space-y-2 cursor-pointer hover:bg-muted/30 transition-colors"
        }
        onClick={() => setShowLightbox(true)}
        title={t("click_to_enlarge", "Click to enlarge")}
      >
        {compact ? (
          hideThumbnail ? (
            // Compact view with hidden thumbnail - show clickable link
            <div className="flex items-center justify-between p-2">
              <div className="flex items-center justify-between gap-2">
                <Image className="w-4 h-4 text-green-600" />
                <span className="text-sm text-primary hover:underline">
                  {t("view_image", "View image")}
                </span>
              </div>
              <Maximize2 className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
          ) : (
            // Compact view - full width image with overlay button
            <div className="relative">
              <img
                src={image.url}
                alt={t("uploaded_image", "Uploaded image")}
                className="w-full max-h-[150px] object-contain rounded-md"
              />
              {/* Overlay button */}
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLightbox(true);
                }}
                className="absolute p-0 transition-opacity top-2 right-2 w-7 h-7 opacity-70 group-hover:opacity-100"
                title={t("view_full_size", "View full size")}
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </Button>
              {showDeleteButton && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteDialog(true);
                  }}
                  className="absolute p-0 transition-opacity top-2 right-11 w-7 h-7 opacity-70 group-hover:opacity-100 text-destructive hover:text-destructive"
                  title={t("delete_image", "Delete image")}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          )
        ) : (
          // Full view with thumbnail
          <>
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Image className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium">
                  {t("image", "Image")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLightbox(true);
                  }}
                  className="w-8 h-8 p-0 text-primary hover:text-primary hover:bg-primary/10"
                  title={t("view_full_size", "View full size")}
                >
                  <Maximize2 className="w-4 h-4" />
                </Button>
                {showDeleteButton && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteDialog(true);
                    }}
                    className="w-8 h-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    title={t("delete_image", "Delete image")}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Thumbnail */}
            <div className="flex items-center justify-center rounded-md bg-background">
              <img
                src={image.url}
                alt={t("uploaded_image", "Uploaded image")}
                className="max-w-full max-h-[150px] object-contain rounded"
              />
            </div>

            {/* Metadata - only show if showMetadata is true */}
            {showMetadata && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {image.width && image.height && (
                  <>
                    <span>
                      {image.width} x {image.height}
                    </span>
                    <span>•</span>
                  </>
                )}
                <span>{formatSize(image.size)}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={showLightbox} onOpenChange={setShowLightbox}>
        <DialogContent className="max-w-2xl p-px [&>button]:hidden">
          <div className="relative group">
            <img
              src={image.url}
              alt={t("uploaded_image", "Uploaded image")}
              className="w-full max-h-[70vh] object-contain rounded-md"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowLightbox(false)}
              className="absolute top-2 right-2 w-7 h-7 p-0 opacity-70 group-hover:opacity-100 transition-opacity"
              title={t("close", "Close")}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("delete_image_title", "Delete Image?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "delete_image_description",
                "Are you sure you want to delete this image? This action cannot be undone.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t("delete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
